// "Configuration des attestations" — one row per document type with the
// institution's rules (actif, validations, paiement complet, rattrapage,
// assiduité, décision du jury). Each toggle saves immediately for this tenant.
import React, { useEffect, useState } from 'react';
import { Loader2, Settings, X } from 'lucide-react';
import {
  getAttestationConfigs,
  updateAttestationConfig,
} from '../../../../services/studentRequestsService';
import {
  AttestationConfigItem,
  AttestationType,
  UpdateAttestationConfigDto,
} from '../../../../types/student-requests.types';

interface AttestationConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onError: (message: string) => void;
  onSaved?: () => void;
}

type ToggleField = keyof Pick<
  AttestationConfigItem,
  | 'is_active'
  | 'requires_finance_validation'
  | 'requires_academic_validation'
  | 'requires_full_payment'
  | 'requires_no_rattrapage'
  | 'requires_good_attendance'
  | 'requires_graduation_status'
>;

const COLUMNS: { field: ToggleField; label: string; hint: string }[] = [
  { field: 'is_active', label: 'Actif', hint: "Proposé aux étudiants" },
  { field: 'requires_finance_validation', label: 'Finance', hint: 'Validation financière requise' },
  { field: 'requires_academic_validation', label: 'Académique', hint: 'Validation académique requise' },
  { field: 'requires_full_payment', label: 'Paiement complet', hint: 'Refusé tant que le solde est dû' },
  { field: 'requires_no_rattrapage', label: 'Sans rattrapage', hint: 'Validation manuelle si un rattrapage est en cours' },
  { field: 'requires_good_attendance', label: 'Assiduité', hint: 'Validation manuelle au-delà du seuil d\'absences' },
  { field: 'requires_graduation_status', label: 'Décision du jury', hint: 'Soumis au Directeur Pédagogique si le jury n\'a pas déclaré admis / diplômé' },
];

const errorMessage = (error: unknown): string => {
  const maybe = error as { response?: { data?: { message?: string | string[] } } };
  const message = maybe.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message ?? 'Erreur lors de la mise à jour de la configuration';
};

export const AttestationConfigModal: React.FC<AttestationConfigModalProps> = ({ isOpen, onClose, onError, onSaved }) => {
  const [configs, setConfigs] = useState<AttestationConfigItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        setLoading(true);
        const data = await getAttestationConfigs();
        if (!cancelled) setConfigs(data);
      } catch (error) {
        if (!cancelled) onError(errorMessage(error));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, onError]);

  if (!isOpen) return null;

  const save = async (type: AttestationType, dto: UpdateAttestationConfigDto, key: string): Promise<void> => {
    try {
      setSavingKey(key);
      const updated = await updateAttestationConfig(type, dto);
      setConfigs((prev) => prev.map((c) => (c.attestation_type === type ? updated : c)));
      onSaved?.();
    } catch (error) {
      onError(errorMessage(error));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={savingKey ? undefined : onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Settings className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Configuration des attestations</h3>
              <p className="text-sm text-gray-500">Règles appliquées à chaque type de document pour votre établissement</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={!!savingKey} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-auto">
          {loading ? (
            <div className="py-12 text-center text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Chargement...
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Type de document</th>
                  {COLUMNS.map((col) => (
                    <th key={col.field} title={col.hint} className="px-3 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {configs.map((config) => (
                  <tr key={config.attestation_type} className={config.is_active ? '' : 'bg-gray-50 text-gray-400'}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{config.label_fr}</div>
                      <div className="text-xs text-gray-500">
                        {config.source === 'default' ? 'Valeurs par défaut' : 'Personnalisé'}
                        {config.minimum_semester_completed ? ` · ${config.minimum_semester_completed} semestre(s) requis` : ''}
                      </div>
                    </td>
                    {COLUMNS.map((col) => {
                      const key = `${config.attestation_type}:${col.field}`;
                      const checked = config[col.field];
                      return (
                        <td key={col.field} className="px-3 py-3 text-center">
                          {savingKey === key ? (
                            <Loader2 className="w-4 h-4 animate-spin mx-auto text-blue-600" />
                          ) : (
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!!savingKey}
                              title={col.hint}
                              onChange={(e) =>
                                void save(config.attestation_type, { [col.field]: e.target.checked }, key)
                              }
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 text-xs text-gray-500">
          Chaque modification est enregistrée immédiatement. Un étudiant non admis n'est jamais refusé automatiquement :
          sa demande est soumise au Directeur Pédagogique, qui voit la décision du jury avant de délivrer le document.
        </div>
      </div>
    </div>
  );
};

export default AttestationConfigModal;
